import { Button, HStack, Separator, VStack } from '@chakra-ui/react';
import { Field as ChakraField } from '@chakra-ui/react/field';
import { ZoneForm, ZoneFormGeometryTypeEnum } from '@components/cockpit/panels/zoneManagement/zoneManagement.types';
import Field from '@components/common/form/Field';
import { PolygonCoordinatesInput } from '@components/common/inputs/PolygonCoordinatesInput';
import { Checkbox } from '@components/ui/checkbox/Checkbox';
import { InputNumber } from '@components/ui/input/InputNumber';
import { FormMap } from '@components/ui/map/formMap/FormMap';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectPlatformByCode } from '@redux/config/config.selectors';
import { getIn, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';

import { ZoneFormDisplayedFormatEnum } from '@/types/map.types';

export default function ZoneFormMap() {
  const { values, errors, touched, setFieldValue, setValues } = useFormikContext<ZoneForm>();

  const selectedPlatform = useAppSelector((state) => selectPlatformByCode(state, values.platform ?? ''));
  const siteAltitude = selectedPlatform?.altitude ?? 0;
  const isHeight = values.displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT;
  const groundLevel = isHeight ? 0 : siteAltitude;

  const isInvalid = (key: keyof ZoneForm) => {
    const isTouched = getIn(touched, key);
    const error = getIn(errors, key);
    return Boolean(isTouched && error);
  };

  const handleHeightToAltitudeChange = () => {
    void setValues((currentValues) => {
      const offset = isHeight ? siteAltitude : -siteAltitude;
      return {
        ...currentValues,
        upperValue: (currentValues.upperValue ?? 0) + offset,
        lowerValue: (currentValues.lowerValue ?? 0) + offset,
        displayedFormat: isHeight ? ZoneFormDisplayedFormatEnum.ALTITUDE : ZoneFormDisplayedFormatEnum.HEIGHT,
      };
    });
  };

  return (
    <FormMap polygon={values.polygon} mode={values.id ? 'edit' : 'create'}>
      <VStack
        position="absolute"
        borderRight="2px solid"
        borderColor="neutral.black"
        backgroundColor="neutral.700"
        left={0}
        height="100%"
        flexShrink={0}
        zIndex={3}
        gap={0.25}
        separator={<Separator size="md" borderColor="neutral.800" marginY={0} />}
      >
        {values.geometry === ZoneFormGeometryTypeEnum.FINITE_HEIGHT && (
          <>
            <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
              <HStack gap={0} width="100%" height="48px">
                <Button
                  variant="toggle"
                  size="toggleM"
                  flex={1}
                  zIndex={values.displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE ? 1 : 0}
                  _hover={{ zIndex: 2 }}
                  data-active={values.displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE}
                  onClick={() =>
                    values.displayedFormat !== ZoneFormDisplayedFormatEnum.ALTITUDE && handleHeightToAltitudeChange()
                  }
                >
                  <FormattedMessage id="global.altitude" />
                </Button>
                <Button
                  variant="toggle"
                  size="toggleM"
                  flex={1}
                  marginLeft="-2px"
                  zIndex={isHeight ? 1 : 0}
                  _hover={{ zIndex: 2 }}
                  data-active={isHeight}
                  onClick={() =>
                    values.displayedFormat !== ZoneFormDisplayedFormatEnum.HEIGHT && handleHeightToAltitudeChange()
                  }
                >
                  <FormattedMessage id="global.height" />
                </Button>
              </HStack>
              <Checkbox
                checked={values.lowerValue === groundLevel}
                label={<FormattedMessage id="components.zones.form.groundHeight" values={{ isHeight }} />}
                onChange={() => values.lowerValue !== groundLevel && setFieldValue('lowerValue', groundLevel)}
              />
            </VStack>
            <HStack gap={0} padding={2} width="100%">
              <ChakraField.Root
                variant="cockpit"
                flexFlow="row"
                alignItems="center"
                required
                invalid={isInvalid('lowerValue')}
              >
                <ChakraField.Label width="100%" height="20px">
                  <FormattedMessage id="components.zones.form.lowerValue" values={{ isHeight }} />
                </ChakraField.Label>
                <Field
                  as={InputNumber}
                  type="text"
                  name="lowerValue"
                  id="lowerValue"
                  width="96px"
                  allowMouseWheel
                  onChange={(value?: number) => setFieldValue('lowerValue', value)}
                  suffix="m"
                />
              </ChakraField.Root>
              <ChakraField.Root
                variant="cockpit"
                flexFlow="row"
                alignItems="center"
                required
                invalid={isInvalid('upperValue')}
              >
                <ChakraField.Label width="100%" height="20px">
                  <FormattedMessage id="components.zones.form.upperValue" values={{ isHeight }} />
                </ChakraField.Label>
                <Field
                  as={InputNumber}
                  type="text"
                  name="upperValue"
                  id="upperValue"
                  width="96px"
                  allowMouseWheel
                  onChange={(value?: number) => setFieldValue('upperValue', value)}
                  suffix="m"
                />
              </ChakraField.Root>
            </HStack>
          </>
        )}
        <PolygonCoordinatesInput />
      </VStack>
    </FormMap>
  );
}
